import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { api, setAccessToken, clearAccessToken } from "@/lib/api";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_plan: string | null;
  is_super_admin: boolean;
  skip_onboarding_redirect: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    organizationName: string,
    email: string,
    name: string,
    password: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  handleGoogleCallback: (exchangeCode: string) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  adoptSession: (accessToken: string, userData: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Try to restore session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Try to refresh token (will use httpOnly cookie)
        const success = await refreshToken();
        if (!success) {
          setIsLoading(false);
        }
      } catch {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await api.post("/auth/refresh", {}, { withCredentials: true });
      const { access_token, user: userData } = response.data;

      setAccessToken(access_token);

      let finalUserData = userData;
      if (userData) {
        setUser(userData);
      } else {
        // If no user data in refresh response, fetch it separately
        try {
          const userResponse = await api.get("/users/me");
          finalUserData = userResponse.data;
          setUser(finalUserData);
        } catch {
          // Failed to fetch user, but token is valid
          console.warn("Failed to fetch user data after token refresh");
        }
      }

      // Set Sentry user context for error tracking
      if (finalUserData) {
        Sentry.setUser({
          id: finalUserData.id,
          email: finalUserData.email,
          username: finalUserData.name,
        });
        if (finalUserData.tenant_id) {
          Sentry.setTag("tenant_id", finalUserData.tenant_id);
        }
      }

      setIsLoading(false);
      return true;
    } catch {
      clearAccessToken();
      setUser(null);
      Sentry.setUser(null);
      setIsLoading(false);
      return false;
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post(
      "/auth/login",
      { email, password },
      { withCredentials: true }
    );

    const { access_token, user: userData } = response.data;

    setAccessToken(access_token);
    setUser(userData);

    // Set Sentry user context for error tracking
    if (userData) {
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.name,
      });
      if (userData.tenant_id) {
        Sentry.setTag("tenant_id", userData.tenant_id);
      }
    }
  }, []);

  const signup = useCallback(
    async (
      organizationName: string,
      email: string,
      name: string,
      password: string
    ) => {
      await api.post("/auth/signup", {
        organization_name: organizationName,
        email,
        name,
        password,
      });
      // User needs to verify email before logging in
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch {
      // Logout failed, but clear local state anyway
    } finally {
      clearAccessToken();
      setUser(null);
      // Clear Sentry user context
      Sentry.setUser(null);
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await api.post("/auth/logout-all", {}, { withCredentials: true });
    } catch {
      // Logout failed, but clear local state anyway
    } finally {
      clearAccessToken();
      setUser(null);
      // Clear Sentry user context
      Sentry.setUser(null);
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const response = await api.get("/auth/google/start");
    const { authorization_url } = response.data;

    // Redirect to Google OAuth
    window.location.href = authorization_url;
  }, []);

  const handleGoogleCallback = useCallback(async (exchangeCode: string) => {
    // Exchange the one-time code for the access token (in the response body) and
    // the refresh-token cookie. The access token is intentionally NOT passed in
    // the callback URL anymore (it would leak via Referer/history/logs).
    const exchangeResponse = await api.post(
      "/auth/exchange-code",
      { exchange_code: exchangeCode },
      { withCredentials: true }
    );

    const token = exchangeResponse.data?.access_token as string | undefined;
    if (!token) {
      throw new Error("No access token returned from exchange");
    }
    setAccessToken(token);

    // Fetch user data after setting token
    try {
      const response = await api.get("/users/me");
      const userData = response.data;
      setUser(userData);

      // Set Sentry user context for error tracking
      if (userData) {
        Sentry.setUser({
          id: userData.id,
          email: userData.email,
          username: userData.name,
        });
        if (userData.tenant_id) {
          Sentry.setTag("tenant_id", userData.tenant_id);
        }
      }
    } catch {
      // Failed to fetch user data
      clearAccessToken();
    }
  }, []);

  // Adopt a session minted by an alternate flow (e.g. OTP sign-in), whose
  // response already carries the access token + user and set the refresh cookie
  const adoptSession = useCallback((accessToken: string, userData: User) => {
    setAccessToken(accessToken);
    setUser(userData);
    if (userData) {
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.name,
      });
      if (userData.tenant_id) {
        Sentry.setTag("tenant_id", userData.tenant_id);
      }
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get("/users/me");
      setUser(response.data);
    } catch {
      // Failed to fetch user data
      console.warn("Failed to refresh user data");
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    logoutAll,
    loginWithGoogle,
    handleGoogleCallback,
    refreshToken,
    refreshUser,
    adoptSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
